import { nextTick, onBeforeUnmount, ref } from "vue";

/**
 * Composable para gerenciar o scroll infinito e IntersectionObserver em tabelas v-data-table/sys-data-table
 * 
 * @param {Object} options
 * @param {Function} options.loadMore - Função para carregar mais dados
 * @param {Ref<Boolean>} options.hasMore - Ref indicando se há mais dados
 * @param {Ref<Boolean>} options.isLoadingMore - Ref indicando se está carregando mais
 * @param {Ref<Boolean>} options.isLoading - Ref indicando se está carregando (inicial)
 * @param {Ref<Array>} options.items - Ref com a lista de itens
 * @param {Number} options.threshold - Distância do fundo para carregar (default 200)
 */
export default function useTableScroll({
  loadMore,
  hasMore,
  isLoadingMore,
  isLoading,
  items,
  threshold = 200
}) {
  const dataTable = ref(null);
  const scrollSentinel = ref(null);
  const scrollContainer = ref(null);
  
  let intersectionObserver = null;
  let tableScrollListenerAdded = false;
  let retryCount = 0;
  const MAX_RETRIES = 10;
  
  let isLoadingFromObserver = false;
  let lastLoadTime = 0;
  const MIN_TIME_BETWEEN_LOADS = 1000;

  const localizarElementoScrollTabela = () => {
    let tableScrollElement = null;

    if (dataTable.value && dataTable.value.$el) {
      const tableEl = dataTable.value.$el;
      tableScrollElement = tableEl.querySelector('#sys-data-table-body') ||
        tableEl.querySelector('.v-data-table__wrapper') ||
        tableEl.querySelector('[class*="wrapper"]') ||
        tableEl.querySelector('[class*="scroll"]');
    }

    if (!tableScrollElement) {
      const seletores = [
        '.wc-table-fixed-column #sys-data-table-body',
        '.wc-table-fixed-column .v-data-table__wrapper',
        '.wc-table-fixed-column [id*="sys-data-table"]',
        '.classe-fixar-coluna-checkbox-tabela-compras #sys-data-table-body',
        '.classe-fixar-coluna-checkbox-tabela-compras .v-data-table__wrapper',
        '.classe-fixar-coluna-checkbox-tabela-compras [id*="sys-data-table"]',
        '#sys-data-table-body',
        '.v-data-table__wrapper',
      ];

      for (const seletor of seletores) {
        const el = document.querySelector(seletor);
        if (el) {
          if (el.scrollHeight > el.clientHeight) {
            tableScrollElement = el;
            break;
          }
          const child = el.querySelector('[class*="wrapper"], [class*="scroll"], [id*="body"]');
          if (child && child.scrollHeight > child.clientHeight) {
            tableScrollElement = child;
            break;
          }
        }
      }
    }

    return tableScrollElement;
  };

  const checkScrollAndLoad = async (scrollTop, scrollHeight, clientHeight) => {
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    if (distanceFromBottom <= threshold && hasMore.value && !isLoadingMore.value && !isLoading.value) {
      await loadMore();
    }
  };

  const handleTableScroll = async (e) => {
    await checkScrollAndLoad(e.target.scrollTop, e.target.scrollHeight, e.target.clientHeight);
  };

  const adicionarListenerTabela = () => {
    if (tableScrollListenerAdded) return;
    if (retryCount >= MAX_RETRIES) return;

    retryCount++;

    nextTick(() => {
      const tableScrollElement = localizarElementoScrollTabela();

      if (tableScrollElement && tableScrollElement.scrollHeight > tableScrollElement.clientHeight) {
        tableScrollElement.removeEventListener('scroll', handleTableScroll);
        tableScrollElement.addEventListener('scroll', handleTableScroll, { passive: true });
        tableScrollListenerAdded = true;
        retryCount = 0;
      } else {
        if (retryCount < MAX_RETRIES) {
          setTimeout(adicionarListenerTabela, 500);
        }
      }
    });
  };

  const configurarIntersectionObserver = () => {
    if (!scrollSentinel.value) return;

    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const now = Date.now();
        const timeSinceLastLoad = now - lastLoadTime;

        if (entry.isIntersecting &&
          hasMore.value &&
          !isLoadingMore.value &&
          !isLoading.value &&
          !isLoadingFromObserver &&
          timeSinceLastLoad >= MIN_TIME_BETWEEN_LOADS &&
          items.value.length >= 40) {

          isLoadingFromObserver = true;
          lastLoadTime = now;

          loadMore().finally(() => {
            setTimeout(() => {
              isLoadingFromObserver = false;
            }, 1000);
          });
        }
      });
    }, {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    });

    intersectionObserver.observe(scrollSentinel.value);
  };

  const resetScrollPosition = () => {
    nextTick(() => {
      const scrollElement = localizarElementoScrollTabela();
      if (scrollElement) {
        scrollElement.scrollTop = 0;
      }
    });
  };

  const onItemsChange = (newLength, oldLength) => {
    if (newLength > 0) {
      tableScrollListenerAdded = false;
      retryCount = 0;
      setTimeout(() => {
        adicionarListenerTabela();
        if (newLength >= 40 && oldLength !== undefined && newLength > oldLength) {
          nextTick(configurarIntersectionObserver);
        }
      }, 500);
    }
  };

  const onVisibleChange = (visible) => {
    if (visible) {
      tableScrollListenerAdded = false;
      retryCount = 0;
      isLoadingFromObserver = false;
      lastLoadTime = 0;
      setTimeout(adicionarListenerTabela, 800);
    } else {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }
      isLoadingFromObserver = false;
      lastLoadTime = 0;
    }
  };

  onBeforeUnmount(() => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
  });

  return {
    dataTable,
    scrollSentinel,
    scrollContainer,
    adicionarListenerTabela,
    configurarIntersectionObserver,
    resetScrollPosition,
    onItemsChange,
    onVisibleChange
  };
}
